import { motion } from "framer-motion";

import { cn } from "#app/utils/misc.tsx";

/**
 * No `icon`: the design's pill tabs are label-only. The prop existed and every
 * caller filled it, which put a glyph in front of "Workgraph" / "Auto app" /
 * "Audit Trail" that the design never had — dropping it from the type keeps a
 * new caller from reintroducing one.
 */
export type PillTab<T extends string> = {
  value: T;
  label: string;
  /** Optional icon rendered before the label. */
  icon?: React.ReactNode;
  /** Anchors a tour step to this tab (see components/tours). */
  dataTour?: string;
  /** Optional count chip (e.g. Source check gaps). */
  badge?: number;
  /**
   * Unselectable, and visibly so. A tab whose handler silently ignores the
   * click reads as broken; say why with `disabledReason`, which becomes the
   * tooltip.
   */
  disabled?: boolean;
  /** Tooltip shown when `disabled`, explaining what would unblock the tab. */
  disabledReason?: string;
};

type PillTabsProps<T extends string> = {
  tabs: PillTab<T>[];
  value: T;
  onValueChange: (value: T) => void;
  /**
   * Must be unique per mounted instance. Framer shares the sliding indicator
   * across every element with the same layoutId, so two pill rows on screen at
   * once (operator detail + chat right panel) would fight over one pill.
   */
  layoutId: string;
  className?: string;
  /** Stretch the row and give each tab equal width (antd Segmented `block`). */
  block?: boolean;
  /**
   * `gradient` — brand purple pill (chat / canvas).
   * `primary` — solid `bg-primary` black, matching default modal Buttons.
   */
  variant?: "gradient" | "primary";
};

/** The design's pill tab row. */
export function PillTabs<T extends string>({
  tabs,
  value,
  onValueChange,
  layoutId,
  className,
  block = false,
  variant = "gradient",
}: PillTabsProps<T>) {
  return (
    <div
      role="tablist"
      className={cn(
        "flex items-center gap-1 rounded-full bg-zinc-200/60 p-1.5 dark:bg-zinc-800",
        block && "w-full",
        className,
      )}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;

        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-tour={tab.dataTour}
            disabled={tab.disabled}
            title={tab.disabled ? tab.disabledReason : undefined}
            onClick={() => {
              if (!tab.disabled) onValueChange(tab.value);
            }}
            // `inline-flex items-center justify-center` on the BUTTON, not just
            // on the inner span. A button is not a flex container by default, so
            // its inline-flex child sat on the button's text baseline and the
            // descender space below pushed the label a couple of pixels above
            // the pill's true centre — visible as soon as the gradient
            // background landed behind it.
            className={cn(
              "relative inline-flex cursor-pointer items-center justify-center rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
              tab.disabled && "cursor-not-allowed opacity-50",
              block && "min-w-0 flex-1",
            )}
          >
            {active ? (
              <motion.span
                layoutId={layoutId}
                className={cn(
                  "absolute inset-0 rounded-full",
                  variant === "primary"
                    ? "bg-primary"
                    : "bg-linear-to-r from-[#5b66aa] to-[#909cca]",
                )}
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            ) : null}

            <span
              className={cn(
                "relative z-10 inline-flex items-center gap-1.5 transition-colors",
                active
                  ? variant === "primary"
                    ? "text-primary-foreground"
                    : "text-white"
                  : "text-zinc-700 hover:text-zinc-900 dark:text-zinc-100 dark:hover:text-white",
              )}
            >
              {tab.icon}
              {tab.label}
              {tab.badge ? (
                <span
                  className={cn(
                    "rounded-full px-1.5 text-[10px] font-medium tabular-nums",
                    active
                      ? "bg-white/20 text-white"
                      : "bg-destructive/15 text-destructive",
                  )}
                >
                  {tab.badge}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default PillTabs;
